import { getBezierPath } from 'reactflow';

const edgeAnimationStyle = `
  @keyframes edgeFlowSourceToTarget {
    from { stroke-dashoffset: 0; }
    to { stroke-dashoffset: -16; }
  }
  .edge-flow-animation {
    animation: edgeFlowSourceToTarget 0.6s linear infinite;
  }
`;

export function DashedEdge({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, selected, data, onEdgeDelete, onEdgeAdd }) {
  const [edgePath] = getBezierPath({ 
    sourceX, 
    sourceY, 
    targetX, 
    targetY,
    sourcePosition,
    targetPosition,
    curvature: 0.25
  });
  
  // Calculate midpoint for button positioning
  const midX = (sourceX + targetX) / 2;
  const midY = (sourceY + targetY) / 2;

  const handleDashedDelete = (e) => {
    e.stopPropagation();
    if (data?.onEdgeDelete) {
      data.onEdgeDelete(id);
    } else {
      // Fallback to window event for backward compatibility
      window.dispatchEvent(new CustomEvent('delete-edge', {
        detail: { edgeId: id }
      }));
    }
  };

  // Determine edge color: green when target is executing, red when selected, else blue
  const targetExecuting = data?.targetExecuting;
  const edgeColor = targetExecuting ? '#22c55e' : selected ? '#ef4444' : '#1976d2';

  return (
    <g>
      <style dangerouslySetInnerHTML={{ __html: edgeAnimationStyle }} />
      {/* Invisible wider stroke for better clickability */}
      <path
        style={{
          stroke: 'transparent',
          strokeWidth: 12,
          opacity: 0,
          fill: 'none',
          cursor: 'pointer'
        }}
        className="react-flow__edge-path"
        d={edgePath}
      />
      
      {/* Dashed path - animated flow when target node is executing (green) */}
      <path
        id={id}
        style={{
          stroke: edgeColor,
          strokeWidth: 1.8,
          opacity: 0.9,
          strokeDasharray: targetExecuting ? '8,8' : '6,2',
          fill: 'none',
          cursor: 'pointer',
          strokeLinecap: 'round',
          strokeLinejoin: 'round',
          shapeRendering: 'geometricPrecision'
        }}
        className={`react-flow__edge-path${targetExecuting ? ' edge-flow-animation' : ''}`}
        d={edgePath}
      />

      {/* Delete button that appears only when edge is selected */}
      {selected && (
        <g transform={`translate(${midX}, ${midY})`}>
          <circle
            r="14"
            fill="#fff"
            stroke="#6c757d"
            strokeWidth="2"
            className="edge-button delete"
            style={{ cursor: 'pointer' }}
          />
          <foreignObject
            x="-8"
            y="-8"
            width="16"
            height="16"
            onClick={(e) => handleDashedDelete(e)}
          >
            <div
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none'
              }}
            >
              <i
                className="fas fa-trash"
                style={{
                  color: '#6c757d',
                  fontSize: '10px',
                  pointerEvents: 'none'
                }}
              ></i>
            </div>
          </foreignObject>
        </g>
      )}
    </g>
  );
}

// Custom Line Edge
export function LineEdge({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, selected, data, onEdgeDelete, onEdgeAdd }) {
  const [edgePath] = getBezierPath({ 
    sourceX, 
    sourceY, 
    targetX, 
    targetY,
    sourcePosition,
    targetPosition,
    curvature: 0.25
  });
  
  // Calculate midpoint for button positioning
  const midX = (sourceX + targetX) / 2;
  const midY = (sourceY + targetY) / 2;

  const handleLineDelete = (e) => {
    e.stopPropagation();
    if (data?.onEdgeDelete) {
      data.onEdgeDelete(id);
    } else {
      // Fallback to window event for backward compatibility
      window.dispatchEvent(new CustomEvent('delete-edge', {
        detail: { edgeId: id }
      }));
    }
  };

  // Determine edge color: green when target is executing, red when selected, else blue
  const targetExecuting = data?.targetExecuting;
  const edgeColor = targetExecuting ? '#22c55e' : selected ? '#ef4444' : '#1976d2';

  return (
    <g>
      <style dangerouslySetInnerHTML={{ __html: edgeAnimationStyle }} />
      {/* Invisible wider stroke for better clickability */}
      <path
        style={{
          stroke: 'transparent',
          strokeWidth: 12,
          opacity: 0,
          fill: 'none',
          cursor: 'pointer'
        }}
        className="react-flow__edge-path"
        d={edgePath}
      />
      
      {/* Path: dashed + flow animation when target executing, else solid */}
      <path
        id={id}
        style={{
          stroke: edgeColor,
          strokeWidth: 1.8,
          padding: 20,
          opacity: 0.9,
          strokeDasharray: targetExecuting ? '8,8' : 'none',
          fill: 'none',
          cursor: 'pointer',
          strokeLinecap: 'round',
          strokeLinejoin: 'round',
          shapeRendering: 'geometricPrecision'
        }}
        className={`react-flow__edge-path${targetExecuting ? ' edge-flow-animation' : ''}`}
        d={edgePath}
      />

      {/* Delete button that appears only when edge is selected */}
      {selected && (
        <g transform={`translate(${midX}, ${midY})`}>
          <circle
            r="14"
            fill="#fff"
            stroke="#6c757d"
            strokeWidth="2"
            className="edge-button delete"
            style={{ cursor: 'pointer' }}
          />
          <foreignObject x="-8" y="-8" width="16" height="16" onClick={(e) => handleLineDelete(e)}>
            <div style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}>
              <i className="fas fa-trash" style={{ 
                color: '#6c757d', 
                fontSize: '10px',
                pointerEvents: 'none'
              } }></i>
            </div>
          </foreignObject>
        </g>
     )}
    </g>  
  );
}

// Custom Edge for Parallel/Sequential
export function CustomEdge({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, data }) {
  const [edgePath] = getBezierPath({ 
    sourceX, 
    sourceY, 
    targetX, 
    targetY,
    sourcePosition,
    targetPosition,
    curvature: 0.25
  });
  
  const targetExecuting = data?.targetExecuting;
  let color = targetExecuting ? '#22c55e' : '#a259e6';
  let strokeDasharray = targetExecuting ? '8,8' : 'none';
  let strokeWidth = 1.8;

  if (!targetExecuting) {
    if (data?.groupType === 'parallel') {
      color = '#1976d2';
      strokeDasharray = '6,4';
    } else if (data?.groupType === 'sequential') {
      color = '#fb8c00';
      strokeDasharray = 'none';
    }
  }

  // Calculate midpoint for button positioning
  const midX = (sourceX + targetX) / 2;
  const midY = (sourceY + targetY) / 2;

  const handleDelete = (e) => {
    e.stopPropagation();
    if (data?.onEdgeDelete) {
      data.onEdgeDelete(id);
    } else {
      // Fallback to window event for backward compatibility
      window.dispatchEvent(new CustomEvent('delete-edge', {
        detail: { edgeId: id }
      }));
    }
  };

  return (
    <g>
      <style dangerouslySetInnerHTML={{ __html: edgeAnimationStyle }} />
      {/* Invisible wider stroke for better clickability */}
      <path
        style={{
          stroke: 'transparent',
          strokeWidth: 12,
          opacity: 0,
          fill: 'none',
          cursor: 'pointer'
        }}
        className="react-flow__edge-path"
        d={edgePath}
      />
      
      <path
        id={id}
        style={{ 
          stroke: color, 
          strokeWidth, 
          strokeDasharray, 
          fill: 'none',
          cursor: 'pointer',
          opacity: data?.selected ? 1 : 0.9,
          strokeLinecap: 'round',
          strokeLinejoin: 'round',
          shapeRendering: 'geometricPrecision'
        }}
        className={`react-flow__edge-path${targetExecuting ? ' edge-flow-animation' : ''}`}
        d={edgePath}
      />
      {/* Optionally, add a label for parallel/sequential */}
      {data?.groupType && (
        <text>
          <textPath href={`#${id}`} startOffset="50%" textAnchor="middle" fill={color} fontSize={13} fontWeight={600} dy={-6}>
            {data.groupType === 'parallel' ? 'Parallel' : 'Sequential'}
          </textPath>
        </text>
      )}

      {/* Delete button that appears only when edge is selected */}
      {data?.selected && (
        <g transform={`translate(${midX}, ${midY})`}>
          {/* Background circle that the line passes through */}
          <circle
            r="14"
            fill="#fff"
            stroke="#6c757d"
            strokeWidth="2"
            className="edge-button delete"
            style={{ cursor: 'pointer' }}
          />
          {/* Delete icon using Font Awesome */}
          <foreignObject x="-8" y="-8" width="16" height="16" onClick={(e) => handleDelete(e)}>
            <div style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none'
            }}>
              <i className="fas fa-trash" style={{ 
                color: '#6c757d', 
                fontSize: '10px',
                pointerEvents: 'none'
              }}></i>
            </div>
          </foreignObject>
        </g>
      )}
    </g>
  );
}
